import React from 'react';

import map from 'lodash/fp/map';
import uppercase from 'lodash/fp/upperCase';

import { ArticleDialog } from '@lumapps/articles/components/ArticleDialog';
import { useArticleDialog } from '@lumapps/articles/hooks/useArticleDialog';
import { selectSpaceOrCommunityId } from '@lumapps/communities/ducks/selectors';
import { CONTRIBUTION_FIELD } from '@lumapps/communities/keys';
import { RenderingType } from '@lumapps/communities/types';
import { doesCurrentUserHaveAccessToCustomContentTypes } from '@lumapps/content-types/ducks/selectors';
import { useCustomContentTypes } from '@lumapps/content-types/hooks/useCustomContentTypes';
import { EventDialog } from '@lumapps/events/components/EventDialog';
import { useEventDialog } from '@lumapps/events/hooks/useEventDialog';
import { mdiCalendar, mdiNewspaper } from '@lumapps/lumx/icons';
import { isInstanceSuperAdmin } from '@lumapps/permissions';
import { PostDialog } from '@lumapps/posts/components/PostDialog';
import { POST_TYPE_ICONS, POST_TYPE_LABEL_KEYS, POST_TYPE_LONG_LABEL_KEYS } from '@lumapps/posts/constants';
import { usePostDialog } from '@lumapps/posts/hooks/usePostDialog';
import { PostType } from '@lumapps/posts/types';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL } from '@lumapps/translations';

import { isContentInSpaceFeatureEnabled, selectSpaceData } from '../ducks/selectors';
import { SpaceServices } from '../types';

export const useSpaceContributionButton = (triggerRef?: React.RefObject<HTMLButtonElement>) => {
    const space = useSelector(selectSpaceData);
    const containerId = useSelector(selectSpaceOrCommunityId) as string;
    const isContentInSpaceEnabled = useSelector(isContentInSpaceFeatureEnabled);
    const doesCurrentUserHaveAccessToCCT = useSelector(doesCurrentUserHaveAccessToCustomContentTypes);
    const isSuperAdmin = useSelector(isInstanceSuperAdmin);

    // Fetch custom content types if feature flag is enabled
    const { parentCustomContentTypes } = useCustomContentTypes({});

    const isPostEnabled = space?.enabledServices.includes(SpaceServices.posts);
    const canCreatePost = space?.myPermissions.canContribute;

    const availablePostTypes = map(uppercase, space?.postTypes ?? []) as PostType[];
    const isDefaultPostTypeAvailable = availablePostTypes.includes(PostType.DEFAULT);
    const isQuestionPostTypeAvailable = availablePostTypes.includes(PostType.QUESTION);
    const isIdeaPostTypeAvailable = availablePostTypes.includes(PostType.IDEA);

    const isArticleEnabled = space?.enabledServices.includes(SpaceServices.article);
    const canCreateArticle = space?.myPermissions.canCreateArticle;

    const isEventEnabled = space?.enabledServices.includes(SpaceServices.event);
    const canCreateEvent = space?.myPermissions.canCreateEvent;

    const shouldDisplayPost = isPostEnabled && isDefaultPostTypeAvailable && canCreatePost;
    const shouldDisplayQuestion = isPostEnabled && isQuestionPostTypeAvailable && canCreatePost;
    const shouldDisplayIdea = isPostEnabled && isIdeaPostTypeAvailable && canCreatePost;
    const shouldDisplayArticle = isArticleEnabled && canCreateArticle;
    const shouldDisplayEvent = isEventEnabled && canCreateEvent;

    const { closePostDialog, createPost, isOpen: isPostDialogOpen } = usePostDialog({});
    const openPostDialog = React.useCallback(
        async (postType: PostType) => {
            await createPost({ postType, renderingType: RenderingType.space, containerId });
        },
        [createPost, containerId],
    );

    const { isOpen: isArticleDialogOpen, openArticleDialog, closeArticleDialog } = useArticleDialog();

    const { closeEventDialog, isOpen: isEventDialogOpen, openEventDialog } = useEventDialog();

    const menuItems = [];

    if (shouldDisplayPost) {
        menuItems.push({
            key: 'post',
            labelKey: GLOBAL.POST,
            tooltipLabelKey: POST_TYPE_LONG_LABEL_KEYS[PostType.DEFAULT],
            icon: POST_TYPE_ICONS[PostType.DEFAULT],
            onClick: () => openPostDialog(PostType.DEFAULT),
        });
    }

    if (shouldDisplayQuestion) {
        menuItems.push({
            key: 'question',
            labelKey: POST_TYPE_LABEL_KEYS[PostType.QUESTION],
            tooltipLabelKey: POST_TYPE_LONG_LABEL_KEYS[PostType.QUESTION],
            icon: POST_TYPE_ICONS[PostType.QUESTION],
            onClick: () => openPostDialog(PostType.QUESTION),
        });
    }

    if (shouldDisplayIdea) {
        menuItems.push({
            key: 'idea',
            labelKey: POST_TYPE_LABEL_KEYS[PostType.IDEA],
            tooltipLabelKey: POST_TYPE_LONG_LABEL_KEYS[PostType.IDEA],
            icon: POST_TYPE_ICONS[PostType.IDEA],
            onClick: () => openPostDialog(PostType.IDEA),
        });
    }

    if (shouldDisplayArticle) {
        menuItems.push({
            key: 'article',
            labelKey: CONTRIBUTION_FIELD.BUTTON_ARTICLE as string,
            tooltipLabelKey: CONTRIBUTION_FIELD.TOOLTIP_ARTICLE as string,
            icon: mdiNewspaper,
            onClick: openArticleDialog,
        });
    }

    if (shouldDisplayEvent) {
        menuItems.push({
            key: 'event',
            labelKey: CONTRIBUTION_FIELD.BUTTON_EVENT as string,
            tooltipLabelKey: CONTRIBUTION_FIELD.TOOLTIP_EVENT as string,
            icon: mdiCalendar,
            onClick: openEventDialog,
        });
    }

    const dialogs =
        shouldDisplayPost ||
        shouldDisplayQuestion ||
        shouldDisplayIdea ||
        shouldDisplayArticle ||
        shouldDisplayEvent ? (
            <>
                {(shouldDisplayPost || shouldDisplayQuestion || shouldDisplayIdea) && (
                    <PostDialog isOpen={isPostDialogOpen} onClose={closePostDialog} triggerElement={triggerRef} />
                )}
                {shouldDisplayArticle && (
                    <ArticleDialog
                        isOpen={isArticleDialogOpen}
                        onCancel={closeArticleDialog}
                        triggerElement={triggerRef}
                    />
                )}
                {shouldDisplayEvent && (
                    <EventDialog isOpen={isEventDialogOpen} onClose={closeEventDialog} triggerElement={triggerRef} />
                )}
            </>
        ) : null;

    // Determine if content types section should be enabled
    const hasAccessToCCT = isSuperAdmin || doesCurrentUserHaveAccessToCCT;
    const hasContentTypes = parentCustomContentTypes && parentCustomContentTypes.length > 0;
    const isSpaceAdmin = space?.myPermissions.canEdit ?? false;
    const shouldShowContentTypesSection = isContentInSpaceEnabled && hasAccessToCCT && hasContentTypes && isSpaceAdmin;

    return {
        menuItems,
        dialogs,
        shouldShowContentTypesSection,
    };
};
